import { useEffect, useState } from 'react'

/**
 * Per-client / per-team accent colors are authored to read on the light page
 * (Ferro & Co. is #101014, Rin Tanaka is #6b0827). Used as *text* on the dark
 * theme they land within a point of the card surface and disappear — the
 * Calendar's event chips were rendering at a 1.06:1 contrast ratio.
 *
 * `readableAccent` leaves a color alone while it clears the threshold and
 * otherwise walks it toward the surface's opposite until it does, so vivid
 * accents stay vivid and only the problem ones get adjusted.
 */

type Rgb = [number, number, number]

function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  let h = m[1] ?? ''
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

function toHex([r, g, b]: Rgb): string {
  return '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')
}

/** WCAG relative luminance. */
function luminance([r, g, b]: Rgb): number {
  const lin = (v: number) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

function contrast(a: Rgb, b: Rgb): number {
  const [l1, l2] = [luminance(a), luminance(b)]
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
}

/** Surfaces the accent chips actually sit on — see `--color-canvas-surface`. */
const SURFACE: Record<'light' | 'dark', Rgb> = {
  light: [255, 255, 255],
  dark: [23, 23, 28],
}

/**
 * Returns `hex` unchanged when it already reads against the theme's card
 * surface, otherwise the closest lightened (dark theme) or darkened (light
 * theme) variant that reaches `minRatio`.
 */
export function readableAccent(hex: string | undefined, theme: 'light' | 'dark', minRatio = 3.5): string {
  const rgb = hex ? parseHex(hex) : null
  if (!rgb) return hex ?? 'currentColor'

  const surface = SURFACE[theme]
  if (contrast(rgb, surface) >= minRatio) return hex as string

  // Push away from the surface: toward white on dark, toward black on light.
  const target: Rgb = theme === 'dark' ? [255, 255, 255] : [0, 0, 0]
  for (let t = 0.1; t <= 1; t += 0.1) {
    const candidate = mix(rgb, target, t)
    if (contrast(candidate, surface) >= minRatio) return toHex(candidate)
  }
  return toHex(target)
}

/**
 * The resolved theme, with `system` collapsed to whatever the OS currently
 * reports — `useAppearance().theme` can be `'system'`, which is not enough to
 * pick a color against.
 */
export function useResolvedTheme(): 'light' | 'dark' {
  const read = (): 'light' | 'dark' => {
    const attr = document.documentElement.getAttribute('data-theme')
    if (attr === 'dark' || attr === 'light') return attr
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  const [theme, setTheme] = useState<'light' | 'dark'>(read)

  useEffect(() => {
    const sync = () => setTheme(read())
    // `data-theme` is set imperatively by AppearanceProvider, so watch the
    // attribute rather than trying to thread the preference through.
    const observer = new MutationObserver(sync)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', sync)
    sync()
    return () => {
      observer.disconnect()
      media.removeEventListener('change', sync)
    }
  }, [])

  return theme
}
